import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useContent } from "../content-context";
import { Eyebrow, Reveal } from "./Reveal";

export default function Testimonials() {
  const { testimonials } = useContent();
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setIndex((i) => (i + 1) % testimonials.length), 6500);
    return () => clearInterval(t);
  }, []);

  const go = (dir) => setIndex((i) => (i + dir + testimonials.length) % testimonials.length);
  const current = testimonials[index];

  return (
    <section data-testid="testimonials-section" className="border-y border-gold/15 bg-navy-2 py-24 md:py-32">
      <div className="mx-auto max-w-4xl px-5 text-center md:px-10">
        <Reveal>
          <div className="flex justify-center">
            <Eyebrow light>Guest Reviews</Eyebrow>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <span className="mt-10 block font-display text-7xl leading-none text-gold/60" aria-hidden="true">&ldquo;</span>
        </Reveal>
        <div className="relative mt-2 min-h-[190px] md:min-h-[220px]" data-testid="testimonial-carousel">
          <AnimatePresence mode="wait">
            <motion.blockquote
              key={index}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -24 }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            >
              <p className="font-display text-2xl font-light italic leading-snug text-ivory md:text-4xl" data-testid="testimonial-quote">
                {current.quote}
              </p>
              <footer className="mt-8 text-xs font-medium uppercase tracking-widest2 text-gold-2" data-testid="testimonial-author">
                {current.name} <span className="text-ivory/40">· {current.source || "Guest"}</span>
              </footer>
            </motion.blockquote>
          </AnimatePresence>
        </div>
        <div className="mt-10 flex items-center justify-center gap-6">
          <button
            data-testid="testimonial-prev"
            aria-label="Previous review"
            onClick={() => go(-1)}
            className="flex h-11 w-11 items-center justify-center border border-ivory/25 text-ivory/70 transition-colors hover:border-gold hover:text-gold-2"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <div className="flex gap-2.5">
            {testimonials.map((_, i) => (
              <button
                key={i}
                data-testid={`testimonial-dot-${i + 1}`}
                aria-label={`Review ${i + 1}`}
                onClick={() => setIndex(i)}
                className={`h-1.5 rounded-full transition-all duration-500 ${i === index ? "w-8 bg-gold" : "w-1.5 bg-ivory/30 hover:bg-ivory/60"}`}
              />
            ))}
          </div>
          <button
            data-testid="testimonial-next"
            aria-label="Next review"
            onClick={() => go(1)}
            className="flex h-11 w-11 items-center justify-center border border-ivory/25 text-ivory/70 transition-colors hover:border-gold hover:text-gold-2"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </section>
  );
}
